import { DateUtils } from "src/utils/date.util";
import { UserLangCode } from "src/utils/util.types";
import { Timestamp } from "firebase/firestore";
import { StatsCategoryRegistry } from "../aggregators/base/category-registry";
import { statisticsRepository } from "../repositories/statistics.repository";
import { Statistics } from "../types/statistics.entity";
import { StateCategory, StatsByCategory } from "../types";

export class QueryHandler {
  async getAllStats(langCode?: UserLangCode): Promise<StatsByCategory> {
    const categories = StatsCategoryRegistry.getAllCategories() as StateCategory[];
    const entries = await Promise.all(
      categories.map(async (category) => {
        const stats = await this.getCategoryStats(category, langCode);
        return [category, stats] as const;
      })
    );
    return Object.fromEntries(entries) as StatsByCategory;
  }

  public getCategoriesExpiry(): Record<StateCategory, number> {
    const categories = StatsCategoryRegistry.getAllCategories();
    return Object.fromEntries(
      categories.map((category) => [category, StatsCategoryRegistry.getCategoryUpdateInfo(category)])
    ) as unknown as Record<StateCategory, number>;
  }

  private async getCategoryStats<C extends StateCategory>(
    category: C,
    langCode?: UserLangCode
  ): Promise<StatsByCategory[C]> {
    const config = StatsCategoryRegistry.getConfig(category);
    if (!config || config.types.length === 0) {
      return [] as StatsByCategory[C];
    }

    const existingStats = await this.getExistingStats(config.types, langCode);

    const expiredTypes = this.getExpiredAndMissedTypes(existingStats, category, config.types);

    const freshStats = expiredTypes.length === 0 ? [] : await config.aggregator.aggregate(expiredTypes, langCode);

    if (freshStats.length > 0) {
      await this.updateStatsInRepository(freshStats);
    }

    const nonExpiredStats = existingStats.filter((stat) => !expiredTypes.includes(stat.type));

    return [...nonExpiredStats, ...freshStats] as StatsByCategory[C];
  }

  private getExpiredAndMissedTypes(
    existingStats: Statistics[],
    category: StateCategory,
    categoryTypes: Array<Statistics["type"]>
  ): Array<Statistics["type"]> {
    const expired = existingStats
      .filter((stat) => !this.checkStatExpiry(stat.updatedAt, category))
      .map((stat) => stat.type);

    const missing = categoryTypes.filter((type) => !existingStats.some((stat) => stat.type === type));

    return [...expired, ...missing];
  }

  private async getExistingStats(types: Statistics["type"][], langCode?: UserLangCode): Promise<Statistics[]> {
    return statisticsRepository.findByTypesAndLangCode({ types, langCode });
  }

  private async updateStatsInRepository(stats: Statistics[]): Promise<void> {
    for (const stat of stats) {
      await statisticsRepository.updateByType(stat.type, stat);
    }
  }

  private checkStatExpiry(updatedAt: Timestamp, category: StateCategory): boolean {
    const config = StatsCategoryRegistry.getConfig(category);
    if (!config) return true;

    const expiryDate = DateUtils.add(updatedAt, config.expiryHours, "hour");
    return DateUtils.checkIsExpiredDate(DateUtils.now(), expiryDate);
  }
}

export const statsQueryHandler = new QueryHandler();
